import React, { FC, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../styles/colors';
import { DefaultInput } from '../general/forms/DefaultInput';
import { SelectBox } from '../general/forms/SelectBox';
import { CustomerTypes } from '../../utils/CustomerTypes';
import { MinimalUser } from '../../interfaces/User';
import { Customer } from '../../interfaces/Customer';
import { useShowAlertPopup } from '../../state/hooks/AlertHooks';
import { LinkButton } from '../general/LinkButton';
import { SuggestionDropDown } from '../general/forms/SuggestionDropDown';
import { Table } from '../general/Table';
import {
  CustomerValidationState,
  isValidState,
  getInitialValidationState,
  validate,
} from '../../utils/CustomerValidation';
import { deepCopy } from '../../utils/deepUtils';
import { isEmail, isStringOfWords, isPhoneNumber } from '../../utils/Validators';
import * as AuthStore from '../store/AuthStore';
import { useApiDispatchQueue } from '../../state/ApiDispatchState';
import { DropDown } from '../general/forms/DropDown';
import { Toggle } from '../general/forms/Toggle';
import * as MinimalUsersCache from '../store/MinimalUsersCache';
import { Headline6 } from '../general/Text';

export interface CustomerFormProps {
  customer?: Customer | null;
  showResponsibleConsultantSelection?: boolean;
  setCustomer: (customer: Customer) => void;
  onCancel?: () => void;
  editable?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.white};
  padding: 2rem 1rem;
`;

const Headline = styled.h2`
  display: flex;
  flex-direction: row;
  min-height: 1.5rem;
  margin-bottom: 1rem;
`;

const ErrorText = styled(Headline6)`
  font-size: 0.75rem;
  color: ${Colors.error};
  margin: -.5rem 0 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.white};
`;

const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  border: none;
  margin-bottom: 3rem;

  ${Headline} {
    margin-bottom: -0.5rem;
    color: ${Colors.grayDark};
    font-weight: 700;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  margin-top: 1rem;
  justify-content: flex-start;
  
  & > * {
    margin-right: auto;

    &:first-child {
      margin-right: 0;
    }
  }
`;

export const CustomerForm: FC<CustomerFormProps> = (props) => {
  const { customer = null, setCustomer, onCancel, editable = true } = props;
  const [tempCustomer, setTempCustomer] = useState<Customer | null>(customer);
  const dispatchApiCall = useApiDispatchQueue();

  const setAddresses = (addresses: any[]) => {
    setTempCustomer({ ...tempCustomer, additionalAddresses: addresses || [] });
  };
  const showNotificationBanner = useShowAlertPopup();
  const showResponsibleConsultantSelection = props.showResponsibleConsultantSelection !== false;    // default: true

  const [validationState, setValidationState] = useState<CustomerValidationState>(getInitialValidationState());

  const [consultantList, setConsultantList] = useState<MinimalUser[] | null>(MinimalUsersCache.get());

  useEffect(() => {
    dispatchApiCall({
      method: 'get',
      route: '/api/users/min',
      header: AuthStore.getAuthHeader(),
      onSuccess: (res) => {
        setConsultantList(res.data);
        MinimalUsersCache.set(res.data as MinimalUser[]);
      },
    });
  }, []);

  // update tempCustomer on props change
  useEffect(() => setTempCustomer((customer && deepCopy(customer)) || {}), [customer]);

  const emptyAddress = () => {
    return {
      street: '',
      streetNumber: '',
      zip: '',
      city: '',
      country: '',
    };
  };
  const setTempAddress = (key: string, value: string) => {
    setTempCustomer((tmp: any) => {
      return {
        ...(tmp || {}),
        address: {
          ...(tmp?.address || {}),
          unit: tmp?.address?.unit || 'kWh',
          type: tmp?.address?.type || 'Erdgas',
          supplier: tmp?.address?.supplier || 'DEW21',
          thermalLine: tmp?.address?.thermalLine || false,
          [key]: value,
        },
      };
    });
  };

  const customerUpdatedAt = new Date(customer?.updatedAt);
  const daysRemaining = 365 - Math.floor((new Date().getTime() - customerUpdatedAt.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <>
      <Headline>Kunden {customer === null ? 'Hinzufügen' : 'Bearbeiten'}</Headline>
      <Wrapper>
        <Form>
          <Fieldset>
            {
              daysRemaining < 31 &&
              <ErrorText>Wird in { Math.max(0, daysRemaining) } Tagen gelöscht!</ErrorText>
            }
            <Headline>Kundendaten</Headline>
            <DefaultInput
              id={'name'}
              disabled={!editable}
              onChange={(event) => {
                setTempCustomer({ ...tempCustomer, name: event.target.value });
              }}
              type={'text'}
              label={'Name'}
              placeholder={'Name'}
              value={tempCustomer?.name || ''}
              required={true}
              validate={isStringOfWords}
            />
            <DefaultInput
              id={'email'}
              disabled={!editable}
              onChange={(event) => {
                setTempCustomer({ ...tempCustomer, email: event.target.value });
              }}
              type={'text'}
              label={'E-Mail des Ansprechpartners'}
              placeholder={'max.mustermann@dew21.de'}
              value={tempCustomer?.email || ''}
              required={true}
              hasError={validationState.email}
              validate={isEmail}
            />
            <DefaultInput
              id={'customer-id'}
              disabled={!editable}
              onChange={(event) => {
                setTempCustomer({ ...tempCustomer, customerNumber: event.target.value });
              }}
              type={'text'}
              label={'GP-Nummer'}
              placeholder={'123456789'}
              value={tempCustomer?.customerNumber || ''}
              required={true}
              hasError={validationState.customerNumber}
            />
            <DefaultInput
              id={'contact-person'}
              disabled={!editable}
              onChange={(event) => {
                setTempCustomer({ ...tempCustomer, contactPerson: event.target.value });
              }}
              type={'text'}
              label={'Ansprechpartner'}
              placeholder={'Max Mustermann'}
              value={tempCustomer?.contactPerson || ''}
              required={true}
              hasError={validationState.contactPerson}
              validate={isStringOfWords}
            />
            <DefaultInput
              id={'contact-person-function'}
              disabled={!editable}
              onChange={(event) => {
                setTempCustomer({ ...tempCustomer, contactPersonFunction: event.target.value });
              }}
              type={'text'}
              label={'Funktion des Ansprechpartners'}
              placeholder={'Geschäftsführung/in'}
              value={tempCustomer?.contactPersonFunction || ''}
              required={true}
              hasError={validationState.contactPersonFunction}
              validate={isStringOfWords}
            />
            <DefaultInput
              id={'phone'}
              disabled={!editable}
              onChange={(event) => {
                setTempCustomer({ ...tempCustomer, phone: event.target.value });
              }}
              type={'text'}
              label={'Telefonnummer'}
              placeholder={'+49123456789'}
              value={tempCustomer?.phone || ''}
              required={true}
              hasError={validationState.phone}
              validate={isPhoneNumber}
            />
          </Fieldset>
          <Fieldset>
            <Headline>Adresse</Headline>
            <DefaultInput
              id={'street'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('street', event.target.value);
              }}
              type={'text'}
              label={'Straße'}
              placeholder={'Günter-Samtlebe-Platz'}
              value={tempCustomer?.address?.street || ''}
              required={true}
              hasError={validationState.address.street}
            />
            <DefaultInput
              id={'street-number'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('streetNumber', event.target.value);
              }}
              type={'text'}
              label={'Hausnummer'}
              placeholder={'1'}
              value={tempCustomer?.address?.streetNumber || ''}
              required={true}
              hasError={validationState.address.streetNumber}
            />
            <DefaultInput
              id={'zip'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('zip', event.target.value);
              }}
              type={'text'}
              label={'PLZ'}
              placeholder={'44135'}
              value={tempCustomer?.address?.zip || ''}
              required={true}
              hasError={validationState.address.zip}
            />
            <DefaultInput
              id={'city'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('city', event.target.value);
              }}
              type={'text'}
              label={'Ort'}
              placeholder={'Dortmund'}
              value={tempCustomer?.address?.city || ''}
              required={true}
              hasError={validationState.address.city}
            />
            <DefaultInput
              id={'country'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('country', event.target.value);
              }}
              type={'text'}
              label={'Land'}
              placeholder={'Deutschland'}
              value={tempCustomer?.address?.country || ''}
              required={true}
              hasError={validationState.address.country}
            />
          </Fieldset>
          <Fieldset>
            <Headline>Verbrauch</Headline>
            <DefaultInput
              id={'consumption'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('consumption', event.target.value);
              }}
              type={'text'}
              label={'Jahresverbrauch'}
              placeholder={'5.000'}
              value={tempCustomer?.address?.consumption || ''}
              required={true}
              hasError={validationState.address.consumption}
            />
            <DropDown
              id={'unit'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('unit', event.target.selectedOptions[0].value);
              }}
              type={'text'}
              label={'Einheit'}
              value={tempCustomer?.address?.unit || ''}
              required={true}
              hasError={validationState.address.unit}
              options={[
                { key: 0, value: 'kWh' },
                { key: 1, value: 'Ltr' },
              ]} />
            <DropDown
              id={'type'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('type', event.target.selectedOptions[0].value);
              }}
              type={'text'}
              label={'Energieart'}
              value={tempCustomer?.address?.type || ''}
              required={true}
              hasError={validationState.address.type}
              options={[
                { key: 'Erdgas', value: 'Erdgas' },
                { key: 'Strom', value: 'Strom' },
                { key: 'Öl', value: 'Öl' },
              ]} />
            <DropDown
              id={'supplier'}
              disabled={!editable}
              onChange={(event) => {
                setTempAddress('supplier', event.target.selectedOptions[0].value);
              }}
              type={'text'}
              label={'Versorger'}
              value={tempCustomer?.address?.supplier || ''}
              required={true}
              hasError={validationState.address.supplier}
              options={[
                { key: 0, value: 'DEW21' },
                { key: 1, value: 'Extern' },
                { key: 2, value: 'Gemischt' },
              ]} />
            <Toggle
              id={'thermalLine'}
              disabled={!editable}
              label={'Wärmetrasse'}
              value={tempCustomer?.address?.thermalLine || false}
              onChange={(checked) => {
                setTempAddress('thermalLine', `${checked}`);
              }} />
          </Fieldset>
          <Fieldset>
            <Headline />
            <SelectBox
              multi={false}
              id={'customer-types'}
              disabled={!editable}
              onChange={(data: string[]) => {
                setTempCustomer({
                  ...tempCustomer,
                  type: data[0],
                });
              }}
              label={'Zielgruppe'}
              value={[tempCustomer?.type] || []}
              options={[
                { key: CustomerTypes.BUSINESS, value: 'Gewerbekunden', icon: 'customers-commercial' },
                { key: CustomerTypes.HOUSING_INDUSTRY, value: 'Wohnungswirtschaft', icon: 'customers-housing' },
                { key: CustomerTypes.MAJOR_CUSTOMERS, value: 'Großkunden', icon: 'customers-major' },
              ]}
              required={true}
              hasError={validationState.type}
            />
            {showResponsibleConsultantSelection && editable && tempCustomer && tempCustomer.responsibleConsultant && consultantList && (
              <SuggestionDropDown
                id={'responsible-consultant'}
                label={'Verantwortlicher Berater'}
                onChange={(suggestion) => {
                  setTempCustomer({ ...tempCustomer, responsibleConsultant: suggestion.id });
                }}
                value={(() => {
                  const currentConsultant =
                    consultantList?.find((consultant) => consultant.id === tempCustomer?.responsibleConsultant) || null;

                  if (currentConsultant) {
                    return {
                      id: currentConsultant.id,
                      text: `${currentConsultant.familyName}, ${currentConsultant.firstName}  | E-Mail: ${currentConsultant.email}`,
                    };
                  }

                  return null;
                })()}
                suggestions={consultantList.map((consultant) => {
                  return {
                    id: consultant.id,
                    text: `${consultant.familyName}, ${consultant.firstName} | E-Mail: ${consultant.email}`,
                  };
                })}
                // value={{ id: '', text: '' }}
              />
            )}
          </Fieldset>
          {tempCustomer && tempCustomer.type == 'HOUSING_INDUSTRY' && (
            <Table
              items={tempCustomer?.additionalAddresses || []}
              hasError={validationState?.additionalAddresses}
              setItems={setAddresses}
            />
          )}
        </Form>
      </Wrapper>
      <ButtonWrapper>
        {
          editable
            ?
              <LinkButton
                onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
                  const nextCustomer: Customer = {
                    ...tempCustomer,
                    additionalAddresses: tempCustomer?.type === 'HOUSING_INDUSTRY' ? (tempCustomer?.additionalAddresses || []) : [],
                  }
                  const state = validate(nextCustomer);
                  setValidationState(state);
                  const isValid = isValidState(state);
                  if (isValid) {
                    setCustomer(nextCustomer);
                  } else {
                    showNotificationBanner({
                      error: true,
                      renderContent: () => {
                        return <>Bitte füllen Sie alle Rot markierten Felder aus.</>;
                      },
                    });
                  }
                }}>
                Änderungen speichern
              </LinkButton>
            :
              <span />
        }
        {
          onCancel &&
          <LinkButton
            onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
              setValidationState(getInitialValidationState());
              setTempCustomer(customer);
              onCancel && onCancel();
            }}>
            Abbrechen
          </LinkButton>
        }
      </ButtonWrapper>
    </>
  );
};
